'use client';

import { ChevronRight } from 'lucide-react';
import { FC } from 'react';
import { CommandItem } from '@components/ui/command';
import { cn } from '@utils/cn';

export interface KnowledgeSearchRowProps {
  typeLabel: string;
  title: string;
  subtitle: string;
  onSelect: () => void;
  /** The underlying `SearchHit.id`, used to scroll to/highlight a row from an answer citation click. */
  hitId?: string;
  /** Briefly true right after a citation click, for a scroll-to highlight. */
  highlighted?: boolean;
}

export interface KnowledgeSearchRowData extends KnowledgeSearchRowProps {
  /** `${kind}:${id}` -- unique across every source kind. React's `key` is
   * reserved and never reaches props, so this is a plain list-key field the
   * caller destructures out before spreading the rest onto the component. */
  key: string;
}

/**
 * One search result: uppercase type label, prominent title, muted
 * subtitle, and a decorative trailing arrow, separated by a thin rule
 * (the Outcome hierarchy from docs/plans/2026-09-17-knowledge-search.md).
 * `CommandItem` supplies the listbox-option semantics (role, selection,
 * keyboard activation) -- the arrow is presentational only, and the whole
 * row performs exactly one action.
 */
const KnowledgeSearchRow: FC<KnowledgeSearchRowProps> = ({
  typeLabel,
  title,
  subtitle,
  onSelect,
  hitId,
  highlighted
}) => (
  <CommandItem
    onSelect={onSelect}
    data-hit-id={hitId}
    className={cn(
      'flex items-center gap-4 rounded-none border-b border-border px-4 py-4 last:border-b-0 aria-selected:bg-accent transition-colors',
      highlighted && 'bg-accent'
    )}
  >
    <div className="min-w-0 flex-1">
      <div className="text-column uppercase tracking-wider text-muted-foreground">
        {typeLabel}
      </div>
      <div className="mt-1 truncate text-card-title text-foreground">
        {title}
      </div>
      {subtitle ? (
        <div className="mt-0.5 line-clamp-2 text-caption text-muted-foreground">
          {subtitle}
        </div>
      ) : null}
    </div>
    <ChevronRight
      aria-hidden="true"
      className="size-5 shrink-0 text-muted-foreground"
    />
  </CommandItem>
);

export default KnowledgeSearchRow;
